// Before Arlo comes home, Today becomes a countdown and preparation hub.

import { BookOpen, CalendarHeart, House, ChevronRight, ClipboardCheck, GraduationCap, HeartPulse, MessageSquareQuote, ShoppingBasket } from 'lucide-react';
import { useMemo } from 'react';
import { HOMECOMING } from '../../content/homecoming.ts';
import { RULES } from '../../content/rules.ts';
import { SHOPPING } from '../../content/shopping.ts';
import { useCollection, useLiveStatus, useProfile } from '../../data/hooks.ts';
import { openCare } from '../../domain/care.ts';
import { milestones } from '../../domain/milestones.ts';
import { daysBetween, formatDate, relativeDay, today } from '../../domain/time.ts';
import { navigate } from '../../app/router.ts';
import { Card, CardHeader, ProgressBar } from '../../ui/primitives.tsx';
import { ReadinessCard } from '../settings/Readiness.tsx';

export function Countdown() {
  const profile = useProfile();
  const s = useLiveStatus(60_000);
  const shopping = useCollection('shopping');
  const rules = useCollection('rules');
  const care = useCollection('care');
  const days = profile.homeDate ? daysBetween(today(s.now), profile.homeDate) : undefined;

  const prep = useMemo(() => {
    const have = new Set(shopping.filter((i) => i.have || i.skip).map((i) => i.id));
    const essentials = SHOPPING.flatMap((c) => c.items).filter((i) => i.beforeHome);
    const agreed = rules.filter((r) => r.answer?.trim()).length;
    const beforeHome = openCare(care).filter((c) => c.due && profile.homeDate && c.due <= profile.homeDate);
    return { essentials, gotEssentials: essentials.filter((i) => have.has(i.id)).length, agreed, beforeHome };
  }, [shopping, rules, care, profile.homeDate]);

  const ticks = useCollection('homecoming');
  const plan = useMemo(() => {
    const done = new Set(ticks.filter((t) => t.done).map((t) => t.id));
    const items = HOMECOMING.flatMap((x) => x.items);
    return { total: items.length, done: items.filter((i) => done.has(i.id)).length };
  }, [ticks]);

  const upcoming = profile.dob ? milestones(profile.dob, profile.homeDate).filter((m) => m.date >= today(s.now)).slice(0, 4) : [];

  return (
    <div className="countdown">
      <Card className="countdown-hero">
        <img className="countdown-photo" src={profile.photo ?? './arlo.jpg'} alt={`${profile.name} as a tiny puppy`} />
        <div className="countdown-text">
          <span className="eyebrow">Getting ready for {profile.name}</span>
          {days !== undefined ? (
            <>
              <h1 className="countdown-days">
                {days === 0 ? 'Home today!' : days === 1 ? 'Home tomorrow' : <>{days} days</>}
              </h1>
              <p className="muted">
                {days > 1 ? `until ${profile.name} comes home · ${formatDate(profile.homeDate!, 'long')}` : 'Deep breath. You’ve got this.'}
              </p>
            </>
          ) : (
            <>
              <h1 className="countdown-days">Coming soon</h1>
              <p className="muted">Add his collection date in Settings to start the countdown.</p>
            </>
          )}
          {s.age && <p className="muted">He’s {s.age.label} old today — still with mum learning to be a dog.</p>}
        </div>
      </Card>

      <div className="countdown-grid">
        <ReadinessCard className="countdown-ready" />
        <Card onClick={() => navigate('/homecoming')} className="countdown-plan">
          <CardHeader title="Homecoming plan" icon={House} action={<ChevronRight aria-hidden />} />
          <p className="big-number">
            {plan.done}
            <span> / {plan.total}</span>
          </p>
          <ProgressBar value={plan.total ? plan.done / plan.total : 0} tone="good" />
          <p className="muted small">Final prep, what to ask the breeder, pick-up day, the first night and his first week.</p>
        </Card>
        <Card onClick={() => navigate('/shopping')}>
          <CardHeader title="Shopping essentials" icon={ShoppingBasket} action={<ChevronRight aria-hidden />} />
          <p className="big-number">
            {prep.gotEssentials}
            <span> / {prep.essentials.length}</span>
          </p>
          <ProgressBar value={prep.essentials.length ? prep.gotEssentials / prep.essentials.length : 0} tone="accent" />
          <p className="muted small">Things to have before day one.</p>
        </Card>
        <Card onClick={() => navigate('/training/rules')}>
          <CardHeader title="House rules agreed" icon={MessageSquareQuote} action={<ChevronRight aria-hidden />} />
          <p className="big-number">
            {prep.agreed}
            <span> / {RULES.length}</span>
          </p>
          <ProgressBar value={RULES.length ? prep.agreed / RULES.length : 0} tone="info" />
          <p className="muted small">Sofa? Upstairs? Who does nights? Decide together now — consistency is everything.</p>
        </Card>
        <Card onClick={() => navigate('/training/cues')}>
          <CardHeader title="Same words, same signals" icon={GraduationCap} action={<ChevronRight aria-hidden />} />
          <p>Agree the cue words you’ll both use — “Off” for furniture, “Down” for lying down. Mixed messages are the #1 reason puppies seem to “not listen”.</p>
        </Card>
        <Card onClick={() => navigate('/health')}>
          <CardHeader title="Before he arrives" icon={ClipboardCheck} action={<ChevronRight aria-hidden />} />
          {prep.beforeHome.length ? (
            <ul className="mini-list">
              {prep.beforeHome.slice(0, 5).map((c) => (
                <li key={c.id}>
                  <HeartPulse aria-hidden /> {c.title}
                  {c.due && <span className="muted"> · {relativeDay(c.due, s.now)}</span>}
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">{profile.dob ? 'All pre-arrival jobs done. Nice.' : 'Add his dates in Settings to build the care plan.'}</p>
          )}
        </Card>
        <Card onClick={() => navigate('/guide/before-he-arrives')}>
          <CardHeader title="Read first" icon={BookOpen} action={<ChevronRight aria-hidden />} />
          <ul className="mini-list">
            <li>A puppy in a Glasgow city-centre flat</li>
            <li>Before he arrives — puppy-proofing & breeder questions</li>
            <li>The first day and night</li>
            <li>Toilet training</li>
            <li>Working together</li>
          </ul>
        </Card>
        {upcoming.length > 0 && (
          <Card>
            <CardHeader title="Coming up" icon={CalendarHeart} />
            <ul className="mini-list">
              {upcoming.map((m) => (
                <li key={m.id}>
                  <strong>{m.title}</strong>
                  <span className="muted"> · {formatDate(m.date)}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
    </div>
  );
}
